export const LESSON_CONTENT = {
  "understanding-market": {
    overview:
      "The stock market is a marketplace where buyers and sellers exchange ownership in public companies. Prices move when the balance between buying demand and selling supply changes.",

    objectives: [
      "Understand why stock prices rise and fall.",
      "Recognize the main participants in the market.",
      "Identify the difference between movement and a confirmed opportunity.",
    ],

    concepts: [
      {
        title: "Supply and demand move price",
        text:
          "When buyers are willing to pay higher prices than before, the stock can rise. When sellers accept lower prices, the stock can fall.",
      },
      {
        title: "Different participants have different goals",
        text:
          "Long-term investors, day traders, institutions, market makers, and automated systems may all participate for different reasons.",
      },
      {
        title: "Price movement is not automatically a trade",
        text:
          "A stock moving quickly can attract attention, but a disciplined trader still checks volume, levels, risk, and confirmation before entering.",
      },
    ],

    exampleTitle: "A stock breaks above resistance",

    example:
      "A stock has repeatedly failed near $10.00. It trades above $10.00 with stronger volume, holds the level, and buyers continue supporting the bid. That combination offers more confirmation than simply touching $10.00 for one moment.",

    ninaNote:
      "The AI Assistant should evaluate the reason for the move, the strength of confirmation, nearby risk, and whether the entry still offers a favorable reward compared with the stop.",
  },
  "how-stocks-move": {
  overview:
    "Stock prices move because buyers and sellers continuously compete for available shares. The speed and size of a move depend on demand, supply, volume, liquidity, news, and broader market conditions.",

  objectives: [
    "Understand how buying and selling pressure move price.",
    "Recognize the roles of volume and liquidity.",
    "Identify why news and market conditions can accelerate a move.",
  ],

  concepts: [
    {
      title: "Aggressive buyers can push price higher",
      text:
        "When buyers accept higher asking prices and continue purchasing available shares, price can move upward.",
    },
    {
      title: "Aggressive sellers can push price lower",
      text:
        "When sellers accept lower bids and continue supplying shares, price can move downward.",
    },
    {
      title: "Volume and liquidity affect movement",
      text:
        "Higher volume can confirm participation, while low liquidity can cause larger and faster price changes because fewer shares are available near the current price.",
    },
  ],

  exampleTitle: "News creates stronger buying demand",

  example:
    "A company releases positive news before the market opens. More traders want to buy, volume increases, sellers raise their asking prices, and buyers continue accepting those higher prices. The stock moves upward as demand exceeds the available supply.",

  ninaNote:
    "The AI Assistant should evaluate whether a price move is supported by volume, liquidity, a meaningful catalyst, market direction, and enough remaining reward before approving an entry.",
},

"market-orders": {
  overview:
    "Market orders and limit orders are two common ways to enter or exit a trade. A market order prioritizes immediate execution, while a limit order prioritizes the price you are willing to accept.",

  objectives: [
    "Understand the difference between market and limit orders.",
    "Recognize the trade-off between execution speed and price control.",
    "Identify situations where slippage or missed execution can occur.",
  ],

  concepts: [
    {
      title: "A market order prioritizes execution",
      text:
        "A market order attempts to fill immediately at the best available price. The final execution price may differ from the price displayed when the order was submitted.",
    },
    {
      title: "A limit order prioritizes price",
      text:
        "A limit order sets the highest price a buyer will pay or the lowest price a seller will accept. The order may not fill if the market never reaches that price.",
    },
    {
      title: "Liquidity and spread affect execution",
      text:
        "Wide spreads, low liquidity, fast movement, and market volatility can increase slippage or make a limit order less likely to fill.",
    },
  ],

  exampleTitle: "Choosing between speed and price control",

  example:
    "A stock is quoted at a $5.00 bid and a $5.05 ask. A market buy may fill immediately near the best available ask, but the price could be higher if the stock moves quickly. A limit buy at $5.05 prevents paying more than $5.05, but the order may not fill.",

  ninaNote:
    "The AI Assistant should compare the spread, liquidity, volatility, order size, and urgency before suggesting an order type. It should never treat a displayed quote as a guaranteed execution price.",
},

"bid-ask-spread": {
  overview:
    "The bid is the highest price a buyer is currently willing to pay, the ask is the lowest price a seller is currently willing to accept, and the spread is the difference between them.",

  objectives: [
    "Understand the difference between the bid and the ask.",
    "Calculate and interpret the spread.",
    "Recognize how liquidity, volatility, and order size affect execution.",
  ],

  concepts: [
    {
      title: "The bid represents current buying interest",
      text:
        "The bid shows the highest displayed price buyers are currently offering. Selling immediately usually means accepting available bids.",
    },
    {
      title: "The ask represents current selling interest",
      text:
        "The ask shows the lowest displayed price sellers are currently requesting. Buying immediately usually means paying available asks.",
    },
    {
      title: "The spread is an execution cost",
      text:
        "A wider spread creates more distance between the best buyer and seller. This can increase slippage and make entries and exits less efficient.",
    },
  ],

  exampleTitle: "A wide spread changes the trade risk",

  example:
    "A stock has a bid of $5.00 and an ask of $5.08, creating an $0.08 spread. A trader who buys at the ask would immediately need the bid to rise before the position could be sold without a loss, even before commissions or fees.",

  ninaNote:
    "The AI Assistant should evaluate the bid, ask, spread percentage, available share size, liquidity, volatility, and order urgency before approving an entry. Wide spreads should increase the trade’s risk level.",
},

"volume-liquidity": {
  overview:
    "Volume measures how many shares are traded during a period, while liquidity describes how easily those shares can be bought or sold without causing a large price change.",

  objectives: [
    "Understand the difference between volume and liquidity.",
    "Recognize how low liquidity can increase spreads and slippage.",
    "Identify why strong volume can confirm or weaken a price move.",
  ],

  concepts: [
    {
      title: "Volume measures trading activity",
      text:
        "Volume shows how many shares changed hands during a selected period. Higher volume usually means more market participation.",
    },
    {
      title: "Liquidity measures execution quality",
      text:
        "Liquidity reflects how easily a position can be entered or exited near the expected price. A liquid stock usually has tighter spreads and more available shares.",
    },
    {
      title: "Low liquidity increases risk",
      text:
        "Low liquidity can cause wider spreads, greater slippage, partial fills, and faster price movement when larger orders enter the market.",
    },
  ],

  exampleTitle: "The same order can behave differently",

  example:
    "A trader wants to buy 1,000 shares. In a highly liquid stock, the order may fill close to one price with a tight spread. In a low-liquidity stock, the same order may fill across several price levels and push the average execution price higher.",

  ninaNote:
    "The AI Assistant should evaluate average volume, current relative volume, bid and ask size, spread width, recent volatility, and order size before approving a trade. Low liquidity should reduce position size and increase caution.",
},

"float-share-supply": {
  overview:
    "Stock float is the number of shares generally available for public trading. A smaller float can make a stock move faster because fewer shares may be available to absorb buying or selling pressure.",

  objectives: [
    "Understand the difference between shares outstanding and public float.",
    "Recognize why low-float stocks can be more volatile.",
    "Identify how float, volume, liquidity, and demand interact.",
  ],

  concepts: [
    {
      title: "Float measures tradable share supply",
      text:
        "A company may have many shares outstanding, but not all of them are available for active public trading. Float focuses on the shares that can generally trade in the market.",
    },
    {
      title: "Low float can increase volatility",
      text:
        "When fewer shares are available, strong buying or selling pressure can move price more quickly. This can create opportunity, but it also increases risk.",
    },
    {
      title: "Float must be evaluated with volume and liquidity",
      text:
        "A low-float stock with unusually high volume may move sharply, while a higher-float stock may require much more participation to produce the same percentage move.",
    },
  ],

  exampleTitle: "Limited share supply can accelerate a move",

  example:
    "A stock has a public float of 8 million shares and suddenly trades 20 million shares after major news. Strong demand is competing for a relatively limited supply of tradable shares, which can increase volatility and create rapid price movement.",

  ninaNote:
    "The AI Assistant should evaluate float, current volume, relative volume, spread, liquidity, catalyst quality, halt risk, short interest, and recent price expansion before approving a low-float trade. Lower float should increase the risk level and may require a smaller position size.",
},

"volatility-price-range": {
  overview:
    "Volatility describes how quickly and how far a stock’s price moves. Price range shows the distance between the high and low during a selected period. Higher volatility can create opportunity, but it also increases execution and risk-management challenges.",

  objectives: [
    "Understand the difference between volatility and price range.",
    "Recognize how volatility affects stops and position size.",
    "Identify why fast-moving stocks require greater caution.",
  ],

  concepts: [
    {
      title: "Volatility measures the intensity of price movement",
      text:
        "A highly volatile stock may rise and fall rapidly within a short period. A less volatile stock usually moves more gradually.",
    },
    {
      title: "Price range shows how far price traveled",
      text:
        "The range is the difference between the highest and lowest prices during a selected period. A wider range often reflects stronger movement and greater uncertainty.",
    },
    {
      title: "Higher volatility changes risk management",
      text:
        "Fast-moving stocks may require wider stops, smaller position sizes, and more careful order placement to avoid excessive risk.",
    },
  ],

  exampleTitle: "The same position size can carry different risk",

  example:
    "Stock A normally moves $0.10 during a five-minute candle, while Stock B often moves $0.50. Using the same share size and stop distance on both stocks would create very different risk. The more volatile stock may require fewer shares and a wider stop based on its price structure.",

  ninaNote:
    "The AI Assistant should evaluate recent price range, average true range, current volatility, spread, liquidity, halt risk, and order size before approving a trade. Higher volatility should reduce position size and increase the required reward-to-risk margin.",
},

"average-true-range": {
  overview:
    "Average True Range, or ATR, measures the typical amount a stock moves during a selected period. ATR does not predict direction. It helps traders understand normal movement and plan risk more realistically.",

  objectives: [
    "Understand what ATR measures.",
    "Recognize why ATR does not predict price direction.",
    "Use ATR as part of stop placement, target planning, and position sizing.",
  ],

  concepts: [
    {
      title: "ATR measures typical movement",
      text:
        "ATR estimates the average true price range over a selected number of periods. A higher ATR means the stock has recently been moving more, while a lower ATR means movement has been smaller.",
    },
    {
      title: "ATR measures volatility, not direction",
      text:
        "ATR can rise during strong upward movement or strong downward movement. It shows movement intensity, not whether price is bullish or bearish.",
    },
    {
      title: "ATR can improve risk planning",
      text:
        "ATR can help traders judge whether a stop is too tight, whether a target is realistic, and whether position size should be reduced because of larger price swings.",
    },
  ],

  exampleTitle: "Using ATR to compare two stocks",

  example:
    "Stock A has an ATR of $0.12, while Stock B has an ATR of $0.60. Stock B normally moves much more during the selected period. A trader may need a wider structure-based stop and fewer shares in Stock B to keep dollar risk controlled.",

  ninaNote:
    "The AI Assistant should combine ATR with support, resistance, recent candle structure, spread, liquidity, volatility, and maximum dollar risk. ATR should guide risk planning, but it should not replace a valid trade-invalidation level.",
},

"risk-reward-ratio": {
  overview:
    "Risk-to-reward ratio compares how much a trader plans to lose if the trade fails with how much the trader may gain if the target is reached. It helps determine whether a setup offers enough potential reward for the amount of risk taken.",

  objectives: [
    "Understand how risk-to-reward ratio is calculated.",
    "Recognize why a higher potential reward does not guarantee a good trade.",
    "Use entry, stop, and target levels to evaluate a setup before entering.",
  ],

  concepts: [
    {
      title: "Risk is the distance from entry to stop",
      text:
        "For a long trade, risk per share is usually calculated by subtracting the stop price from the entry price.",
    },
    {
      title: "Reward is the distance from entry to target",
      text:
        "Potential reward per share is usually calculated by subtracting the entry price from the planned target price.",
    },
    {
      title: "The ratio helps compare opportunity with risk",
      text:
        "A 1-to-2 risk-to-reward ratio means the trader is risking one unit to pursue two units of potential reward. The setup still needs confirmation, liquidity, and a realistic target.",
    },
  ],

  exampleTitle: "Calculating a 1-to-2 setup",

  example:
    "A trader plans to enter at $5.20, place a stop at $5.10, and target $5.40. The risk is $0.10 per share and the potential reward is $0.20 per share. That creates a 1-to-2 risk-to-reward ratio.",

  ninaNote:
    "The AI Assistant should calculate risk per share, potential reward, position size, nearby support and resistance, target realism, spread, liquidity, and trade confidence before approving a setup. A favorable ratio should not override weak confirmation or poor execution conditions.",
},

"position-sizing": {
  overview:
    "Position sizing determines how many shares a trader can buy or sell while keeping the planned dollar risk within a defined limit. It connects account protection, stop distance, and trade size.",

  objectives: [
    "Understand how maximum dollar risk controls position size.",
    "Calculate shares using entry price and stop distance.",
    "Recognize why wider stops require fewer shares.",
  ],

  concepts: [
    {
      title: "Maximum dollar risk comes first",
      text:
        "Before entering a trade, the trader decides the most money they are willing to lose if the stop is reached.",
    },
    {
      title: "Risk per share comes from entry and stop",
      text:
        "For a long trade, risk per share is usually calculated by subtracting the stop price from the entry price.",
    },
    {
      title: "Position size adjusts to the stop distance",
      text:
        "Position size is calculated by dividing maximum dollar risk by risk per share. A wider stop creates more risk per share and therefore requires fewer shares.",
    },
  ],

  exampleTitle: "Calculating shares from planned risk",

  example:
    "A trader is willing to risk $50. The planned entry is $5.20 and the stop is $5.10, creating $0.10 of risk per share. Dividing $50 by $0.10 gives a position size of 500 shares.",

  ninaNote:
    "The AI Assistant should calculate risk per share, maximum dollar risk, position size, total position value, spread, slippage, liquidity, buying-power limits, and daily loss exposure before approving a trade. Position size must be reduced when execution risk or volatility is elevated.",
},

"trade-planning": {
  overview:
    "A trade plan defines what must happen before entry, where the trade becomes invalid, how much capital is at risk, and where profits may be taken. Planning before entering helps reduce impulsive decisions.",

  objectives: [
    "Understand the essential parts of a complete trade plan.",
    "Define entry, stop, target, position size, and invalidation before entering.",
    "Recognize why a trade should be skipped when the plan is incomplete.",
  ],

  concepts: [
    {
      title: "The setup must be clearly defined",
      text:
        "A trader should know what pattern, catalyst, market condition, or price behavior creates the opportunity before considering an entry.",
    },
    {
      title: "Risk must be planned before execution",
      text:
        "The stop, risk per share, maximum dollar risk, and position size should be calculated before the order is placed.",
    },
    {
      title: "The exit plan includes both failure and success",
      text:
        "A complete plan identifies where the trade becomes invalid and where partial or full profits may be taken if the setup works.",
    },
  ],

  exampleTitle: "Planning a breakout trade before entry",

  example:
    "A stock is approaching resistance at $5.20. The trader plans to enter only after price breaks and holds above $5.20 with strong volume. The stop is $5.10, the first target is $5.40, the second target is $5.60, and position size is calculated from the $0.10 risk per share.",

  ninaNote:
    "The AI Assistant should require a clear setup, confirmation conditions, entry, stop, targets, position size, reward-to-risk ratio, catalyst, spread, liquidity, and invalidation level before approving a trade. If key parts are missing, The AI Assistant should mark the plan incomplete.",
},

"entry-confirmation": {
  overview:
    "Entry confirmation means waiting for evidence that a planned setup is actually developing before placing the trade. Confirmation helps reduce impulsive entries and false breakouts.",

  objectives: [
    "Understand why a setup and an entry signal are not the same thing.",
    "Recognize useful forms of price, volume, and momentum confirmation.",
    "Identify when market conditions are too weak or risky for entry.",
  ],

  concepts: [
    {
      title: "A setup creates interest, not permission",
      text:
        "A stock approaching support, resistance, VWAP, or a breakout level may be worth watching, but the trader still needs evidence that buyers or sellers are taking control.",
    },
    {
      title: "Confirmation should match the trade idea",
      text:
        "A breakout may require price to move above resistance, hold the level, and show stronger volume. A support trade may require rejection of lower prices and renewed buying pressure.",
    },
    {
      title: "Market conditions can invalidate confirmation",
      text:
        "Weak volume, a wide spread, poor liquidity, a failing market, or nearby resistance can make an otherwise promising entry too risky.",
    },
  ],

  exampleTitle: "Waiting for breakout confirmation",

  example:
    "A stock approaches resistance at $5.20. Instead of buying before the breakout, the trader waits for price to move above $5.20, hold the level, print a strong candle, and show increasing volume. The entry is considered only after those conditions appear.",

  ninaNote:
    "The AI Assistant should require confirmation that matches the setup. It should evaluate price behavior, volume, momentum, spread, liquidity, VWAP, market direction, nearby support and resistance, and remaining reward before approving an entry.",
},

"profit-targets": {
  overview:
    "Profit targets define where a trader plans to reduce or close a position if the trade moves in the expected direction. Good targets are based on market structure, volatility, and realistic reward—not hope.",

  objectives: [
    "Understand how support and resistance can guide profit targets.",
    "Recognize why targets should be realistic for current volatility.",
    "Use partial profits to manage risk while allowing part of a trade to continue.",
  ],

  concepts: [
    {
      title: "Targets should come from market structure",
      text:
        "Nearby resistance, prior highs, support zones, and other important price levels can help identify where buyers or sellers may react.",
    },
    {
      title: "Volatility affects target realism",
      text:
        "A target that is far beyond the stock’s normal price movement may be unrealistic. Recent range and ATR can help estimate whether the target is reasonable.",
    },
    {
      title: "Partial profits can protect gains",
      text:
        "A trader may sell part of the position at the first target, adjust the stop, and allow the remaining shares to continue toward a second target.",
    },
  ],

  exampleTitle: "Using two planned profit targets",

  example:
    "A trader enters at $5.20 with a stop at $5.10. The first resistance area is $5.40 and the next is $5.60. The trader plans to sell part of the position at $5.40, move the stop to protect the trade, and hold the remaining shares for $5.60 if momentum continues.",

  ninaNote:
    "The AI Assistant should evaluate nearby support and resistance, ATR, recent price range, volume, momentum, spread, liquidity, and risk-to-reward before approving profit targets. Targets should be adjusted when market conditions weaken or when expected reward is no longer realistic.",
},

"trailing-stops": {
  overview:
    "A trailing stop is a stop-loss that moves upward as a winning trade advances. Its purpose is to protect part of the open profit while still giving the position room to continue.",

  objectives: [
    "Understand how a trailing stop differs from the original stop-loss.",
    "Recognize common methods for moving a stop as price rises.",
    "Avoid tightening the stop so quickly that normal price movement forces an early exit.",
  ],

  concepts: [
    {
      title: "The original stop protects the initial trade risk",
      text:
        "Before the trade begins, the stop is placed at the level that invalidates the setup. It should not be moved farther away simply because the trade moves against the trader.",
    },
    {
      title: "A trailing stop protects developing profit",
      text:
        "As price moves higher, the stop may be raised below a new support level, higher low, moving average, VWAP, or another planned reference point.",
    },
    {
      title: "The stop still needs breathing room",
      text:
        "Stocks often pull back during an uptrend. A stop placed too close to the current price may exit the trade during normal volatility before the move continues.",
    },
  ],

  exampleTitle: "Moving the stop after the first target",

  example:
    "A trader enters at $5.20 with an original stop at $5.10. Price reaches the first target at $5.40, and the trader takes partial profits. After price forms a higher low at $5.32, the stop on the remaining shares is raised below that level instead of being left at $5.10.",

  ninaNote:
    "The AI Assistant should trail stops using planned market structure, volatility, ATR, higher lows, VWAP, momentum, and remaining reward. It should avoid moving the stop too aggressively and should never widen the stop beyond the original risk without a new approved plan.",
},

"breakouts-false-breakouts": {
  overview:
    "A breakout happens when price moves beyond an important support or resistance level. A false breakout happens when price briefly crosses the level but quickly reverses. Traders should evaluate price behavior, volume, liquidity, and follow-through before entering.",

  objectives: [
    "Understand the difference between a valid breakout and a false breakout.",
    "Recognize confirmation signals that can strengthen a breakout setup.",
    "Identify warning signs that a breakout may be failing.",
  ],

  concepts: [
    {
      title: "A breakout should show acceptance beyond the level",
      text:
        "Price moving above resistance for only a moment is not enough. A stronger breakout may close or hold above the level and continue attracting buyers.",
    },
    {
      title: "Volume and follow-through matter",
      text:
        "Increasing volume and continued price movement can show participation. Weak volume, hesitation, or immediate rejection may signal that the breakout lacks support.",
    },
    {
      title: "False breakouts can trap late entries",
      text:
        "When price breaks a level and quickly reverses, traders who entered late may be forced to exit. Waiting for confirmation can reduce the risk of entering a failed move.",
    },
  ],

  exampleTitle: "Valid breakout versus false breakout",

  example:
    "A stock has resistance at $5.20. In the first attempt, price reaches $5.24 but immediately falls below $5.20 on weak volume. This is a false-breakout warning. Later, price moves above $5.20 with stronger volume, holds the level during a pullback, and continues toward $5.40. The second move provides stronger confirmation.",

  ninaNote:
    "The AI Assistant should evaluate breakout level quality, volume, relative volume, spread, liquidity, candle closes, retests, momentum, nearby resistance, market direction, and follow-through. It should warn when price briefly breaks a level without holding it or when participation begins weakening.",
},

"vwap-basics": {
  overview:
    "VWAP stands for volume-weighted average price. It shows the average price traded during the session while giving more influence to prices where more volume occurred. Day traders often use VWAP to evaluate intraday trend, trade location, and possible support or resistance.",

  objectives: [
    "Understand what VWAP represents.",
    "Recognize how price location above or below VWAP can affect trade context.",
    "Use VWAP together with price action, volume, and market structure instead of treating it as a guaranteed signal.",
  ],

  concepts: [
    {
      title: "VWAP combines price and volume",
      text:
        "VWAP is not a simple average of prices. Prices traded with greater volume have more influence, which helps show where much of the session’s activity has occurred.",
    },
    {
      title: "Price above or below VWAP provides context",
      text:
        "Price holding above VWAP may indicate stronger intraday demand, while price remaining below VWAP may indicate weaker conditions. The slope and repeated reactions around VWAP also matter.",
    },
    {
      title: "VWAP can act as dynamic support or resistance",
      text:
        "In an intraday uptrend, traders may watch for pullbacks that hold VWAP. In a weaker trend, rallies into VWAP may face resistance. These reactions still require confirmation.",
    },
  ],

  exampleTitle: "Using VWAP as trade context",

  example:
    "A stock breaks resistance and trades above a rising VWAP. It later pulls back toward VWAP, volume decreases during the pullback, and buyers defend the area. The trader waits for renewed momentum before considering an entry rather than buying only because price touched VWAP.",

  ninaNote:
    "The AI Assistant should evaluate whether price is above or below VWAP, the direction of the VWAP line, repeated holds or rejections, volume behavior, spread, liquidity, momentum, nearby support and resistance, and overall market direction. VWAP should support the trade thesis, not replace confirmation and risk management.",
},

"level-2-basics": {
  overview:
    "Level 2 displays multiple bid and ask prices from market participants. It can help traders observe liquidity, market depth, spread changes, and possible buying or selling pressure, but displayed orders can change or disappear quickly.",

  objectives: [
    "Understand the difference between the bid side and the ask side.",
    "Recognize how market depth and liquidity appear in Level 2.",
    "Avoid treating large displayed orders as guaranteed support or resistance.",
  ],

  concepts: [
    {
      title: "The bid side represents displayed buying interest",
      text:
        "Bids show prices where market participants are currently willing to buy. Larger size may suggest interest, but orders can be canceled or moved before execution.",
    },
    {
      title: "The ask side represents displayed selling interest",
      text:
        "Asks show prices where market participants are currently willing to sell. A large ask may slow price temporarily, but it does not guarantee that price cannot move higher.",
    },
    {
      title: "Order flow matters more than one static number",
      text:
        "Traders should watch how bids and asks change, whether orders are being executed, how quickly levels refresh, and whether price is accepting or rejecting an area.",
    },
  ],

  exampleTitle: "Reading pressure near resistance",

  example:
    "A stock trades at $5.18 with resistance near $5.20. Level 2 shows several sellers at $5.20, but buyers repeatedly execute those shares and the ask size keeps decreasing. This may support a breakout attempt. If the ask continues refreshing and price cannot advance, the level may remain resistance.",

  ninaNote:
    "The AI Assistant should evaluate bid and ask size, spread, order changes, executions, liquidity, price behavior, volume, and nearby support or resistance. It should warn that displayed orders can be canceled, hidden, or used to mislead, so Level 2 must be combined with actual price action and risk management.",
},

"trading-psychology-discipline": {
  overview:
    "Trading psychology is the ability to make decisions according to a tested plan instead of reacting impulsively to fear, greed, frustration, or excitement. Discipline means consistently following entry, risk, exit, and daily-loss rules even when emotions are strong.",

  objectives: [
    "Recognize common emotions that can damage trading decisions.",
    "Understand how preparation and written rules support discipline.",
    "Use pauses, reviews, and daily limits to reduce impulsive behavior.",
  ],

  concepts: [
    {
      title: "Fear and greed can distort decisions",
      text:
        "Fear may cause a trader to exit a valid setup too early or avoid a qualified opportunity. Greed may lead to oversized positions, late entries, or holding beyond the planned target.",
    },
    {
      title: "Discipline begins before the trade",
      text:
        "A written plan should define the setup, entry confirmation, position size, stop, targets, and conditions that cancel the trade. Decisions become harder to control when these rules are created after entry.",
    },
    {
      title: "Losses require control, not revenge",
      text:
        "After a loss, traders may feel pressure to recover money immediately. A pause, consecutive-loss limit, and daily-loss limit can help prevent emotional revenge trading.",
    },
  ],

  exampleTitle: "Following the plan after a losing trade",

  example:
    "A trader loses on the first setup of the morning. Instead of immediately taking a larger trade to recover the loss, the trader records what happened, waits for the next qualified setup, uses the normal position size, and stops for the day if the daily-loss limit is reached.",

  ninaNote:
    "The AI Assistant should identify emotional-risk behaviors such as chasing, oversized positions, repeated entries after losses, moving stops, ignoring confirmation, and exceeding daily limits. It should encourage a pause, require the checklist again, and block paper or live execution when predefined safety limits are reached.",
},

  "candlestick-basics": {
    overview:
      "A candlestick summarizes price movement during a specific period. Its body shows the opening and closing prices, while its wicks show the highest and lowest prices reached during that period.",

    objectives: [
      "Identify the open, high, low, and close of a candlestick.",
      "Recognize the difference between bullish and bearish candles.",
      "Understand how candle bodies and wicks can reveal buying and selling pressure.",
    ],

    concepts: [
      {
        title: "The body shows the open and close",
        text:
          "The candle body represents the distance between the opening price and closing price. A larger body usually means price moved more strongly during that period.",
      },
      {
        title: "The wicks show rejected prices",
        text:
          "The upper and lower wicks show how far price traveled beyond the body. A long wick may indicate that buyers or sellers rejected that price area.",
      },
      {
        title: "Context matters more than one candle",
        text:
          "A single candle should not be treated as a complete trade signal. Traders should also study trend, support, resistance, volume, VWAP, and nearby risk.",
      },
    ],

    exampleTitle: "A bullish candle forms above support",

    example:
      "A stock pulls back toward support at $5.00, briefly trades down to $4.95, and then closes at $5.18 near the high of the candle. The long lower wick suggests sellers pushed price down, but buyers regained control before the candle closed.",

    ninaNote:
      "The AI Assistant should identify the candle structure, compare it with recent candles, check whether it formed near an important level, and wait for confirmation before treating it as an entry signal.",
  },

  "support-resistance": {
    overview:
      "Support and resistance are price areas where buying or selling pressure has previously caused a noticeable reaction. Traders use these levels to understand where price may pause, reverse, break out, or fail.",

    objectives: [
      "Understand the difference between support and resistance.",
      "Recognize how repeated price reactions can strengthen a level.",
      "Distinguish between a confirmed breakout and a temporary move through a level.",
    ],

    concepts: [
      {
        title: "Support is an area where buyers may respond",
        text:
          "Support forms when buying demand repeatedly appears near a price area and prevents or slows further decline. It is usually treated as a zone rather than one perfect price.",
      },
      {
        title: "Resistance is an area where sellers may respond",
        text:
          "Resistance forms when selling pressure repeatedly appears near a price area and prevents or slows further gains. Multiple failed attempts can make the level more visible to traders.",
      },
      {
        title: "A breakout needs confirmation",
        text:
          "Price moving through resistance for one moment is not enough. Stronger volume, a close above the level, and continued support can provide better confirmation.",
      },
    ],

    exampleTitle: "Resistance becomes support after a breakout",

    example:
      "A stock repeatedly fails near $10.00, then breaks above the level with strong volume and closes at $10.18. It later pulls back toward $10.00, buyers defend the area, and price begins rising again. The old resistance may now be acting as support.",

    ninaNote:
      "The AI Assistant should treat support and resistance as zones, count prior reactions, evaluate volume and liquidity, identify false-breakout risk, and avoid approving an entry when the nearest resistance leaves too little reward.",
  },

  "macd-basics": {
    overview:
      "MACD is a momentum indicator that compares two moving averages. Traders use it to study whether momentum is strengthening, weakening, or changing direction.",

    objectives: [
      "Understand the MACD line, signal line, and histogram.",
      "Recognize bullish and bearish momentum shifts.",
      "Understand why price can rise while MACD falls.",
    ],

    concepts: [
      {
        title: "A crossover is confirmation, not a guarantee",
        text:
          "A bullish crossover occurs when the MACD line moves above the signal line. It can support a trade idea, but price structure, volume, and risk still matter.",
      },
      {
        title: "The histogram shows momentum distance",
        text:
          "Growing histogram bars can indicate increasing momentum. Shrinking bars can warn that momentum is weakening even before a crossover occurs.",
      },
      {
        title: "Price and MACD can diverge",
        text:
          "Price may continue making higher highs while MACD makes lower highs. This suggests price is still rising, but the strength behind the move may be fading.",
      },
    ],

    exampleTitle: "Price rises while MACD weakens",

    example:
      "A stock climbs from $5.00 to $5.40, but each MACD histogram bar becomes smaller. Buyers remain in control of price, yet momentum is slowing. A trader may protect profit instead of assuming the move will continue at the same speed.",

    ninaNote:
      "The AI Assistant should never use MACD alone. It should combine momentum with VWAP, support and resistance, volume, price action, and the current market environment.",
  },

  "stop-loss": {
    overview:
      "A stop loss defines where a trade idea is no longer valid. Its purpose is to protect capital and prevent one losing trade from becoming an uncontrolled loss.",

    objectives: [
      "Place stops based on trade structure rather than emotion.",
      "Calculate position size from the distance to the stop.",
      "Understand why stops that are too tight or too wide can fail.",
    ],

    concepts: [
      {
        title: "The stop belongs beyond invalidation",
        text:
          "For a long trade, the stop is often placed below meaningful support or the setup’s failure point—not at a random dollar amount.",
      },
      {
        title: "Position size follows the stop",
        text:
          "Determine the maximum dollars you are willing to lose, then divide that amount by the risk per share.",
      },
      {
        title: "Never move a stop farther to avoid a loss",
        text:
          "Moving the stop farther away increases risk after the trade has already weakened. A disciplined plan defines risk before entry.",
      },
    ],

    exampleTitle: "Position sizing from a planned stop",

    example:
      "A trader plans to enter at $5.20 with a stop at $5.10. The risk is $0.10 per share. With a maximum planned loss of $50, the calculated position size is 500 shares.",

    ninaNote:
      "The AI Assistant should calculate the stop, dollar risk, position size, reward-to-risk ratio, and daily loss exposure before approving a trade.",
  },

  "short-selling": {
    overview:
      "Short selling allows a trader to attempt to profit from a declining stock by borrowing shares, selling them, and later buying them back.",

    objectives: [
      "Understand how a short position is opened and closed.",
      "Recognize borrow availability and short-sale restrictions.",
      "Understand why short selling can carry unusually high risk.",
    ],

    concepts: [
      {
        title: "A short sale begins with borrowed shares",
        text:
          "The broker must generally locate shares that can be borrowed. Some stocks may be unavailable or expensive to borrow.",
      },
      {
        title: "The position closes with a buy-to-cover order",
        text:
          "A short seller profits when shares are repurchased below the original sale price, before fees and borrowing costs.",
      },
      {
        title: "Loss potential can be very large",
        text:
          "A stock can theoretically keep rising, which means a short position does not have the same natural maximum loss as a long stock position.",
      },
    ],

    exampleTitle: "Shorting a failed breakout",

    example:
      "A stock breaks above resistance but quickly loses the level, volume fades, and sellers take control. A trained trader may consider a short only after confirming borrow availability, defining a stop, and avoiding entry during a halt or extreme volatility.",

    ninaNote:
      "The AI Assistant should treat short selling as advanced and high risk. Borrow status, spread, liquidity, volatility, halt risk, stop placement, and market direction must all be checked.",
  },

  "relative-volume": {
  overview:
    "Relative volume compares a stock’s current trading activity with its typical activity for the same period. It helps traders identify whether participation is unusually strong or weak.",

  objectives: [
    "Understand what relative volume measures.",
    "Interpret relative-volume values above or below 1.0.",
    "Recognize why unusual participation can strengthen or weaken a setup.",
  ],

  concepts: [
    {
      title: "Relative volume compares current activity with normal activity",
      text:
        "A relative-volume reading of 1.0 means current volume is close to normal. A reading above 1.0 means activity is stronger than usual, while a reading below 1.0 means activity is weaker than usual.",
    },
    {
      title: "Higher relative volume can confirm interest",
      text:
        "A breakout or momentum move supported by unusually high relative volume may have stronger participation than a similar move occurring on average or low volume.",
    },
    {
      title: "Relative volume does not guarantee direction",
      text:
        "High relative volume can appear during strong buying or strong selling. Traders must still evaluate price action, trend, catalyst, support, resistance, and risk.",
    },
  ],

  exampleTitle: "Comparing normal activity with unusual activity",

  example:
    "A stock normally trades 500,000 shares by 10:00 AM. Today it has already traded 1,500,000 shares by that time. Its relative volume is approximately 3.0, meaning activity is about three times stronger than normal.",

  ninaNote:
    "The AI Assistant should compare current volume with typical volume for the same time of day. It should combine relative volume with catalyst quality, price direction, spread, liquidity, volatility, and nearby support or resistance before approving a trade.",
},
};

export function getLessonContent(lessonId) {
  return LESSON_CONTENT[lessonId] || null;
}